import React, { useEffect, useState } from 'react';
import { FaClock, FaMapMarkerAlt, FaCheck, FaTimes, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formatPrice } from '../../utils/currency';
import { orderTrackingService } from '../../services/apiService';

function formatCountdown(ms) {
  if (ms <= 0) return '0m';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function resolveExpiryMs(offer) {
  const r = offer?.reassignment;
  if (!r) return 0;
  if (offer.phase === 'seller' && r.replacementSellerSlaDeadline) {
    return new Date(r.replacementSellerSlaDeadline).getTime() - Date.now();
  }
  if (r.buyerExpiresAt) {
    return new Date(r.buyerExpiresAt).getTime() - Date.now();
  }
  return 0;
}

/**
 * Buyer 24h reassignment offer + seller 2h acceptance wait (Scenarios 1, 2, 4).
 */
export default function ReassignmentOfferCard({ orderId, onUpdated }) {
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [remaining, setRemaining] = useState(0);

  const load = async () => {
    try {
      const data = await orderTrackingService.getReassignmentOffer(orderId);
      setOffer(data?.pending ? data : null);
      if (data?.pending) {
        setRemaining(resolveExpiryMs(data));
      }
    } catch {
      setOffer(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [orderId]);

  useEffect(() => {
    if (!offer?.pending) return undefined;
    const id = setInterval(() => {
      setRemaining(resolveExpiryMs(offer));
    }, 1000);
    return () => clearInterval(id);
  }, [offer]);

  if (loading || !offer?.reassignment) return null;

  const r = offer.reassignment;
  const isSellerPhase = offer.phase === 'seller';
  const priceDiff = Number(r.priceDiff || 0);
  const needsApproval = priceDiff > 0;

  const handleAccept = async () => {
    setActing(true);
    try {
      await orderTrackingService.acceptReassignment(orderId, {
        approvePriceDiff: needsApproval,
      });
      toast.success('Replacement seller assigned — they have 2h to accept');
      await onUpdated?.();
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not accept replacement');
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    setActing(true);
    try {
      await orderTrackingService.rejectReassignment(orderId, 'Buyer declined replacement');
      toast.info('Reassignment declined — refund initiated');
      await onUpdated?.();
      setOffer(null);
    } finally {
      setActing(false);
    }
  };

  if (isSellerPhase) {
    return (
      <section className="mt-6 rounded-2xl border border-amber-600/50 bg-amber-950/25 p-5">
        <p className="text-sm font-bold uppercase tracking-wide text-amber-300">
          Awaiting replacement seller
        </p>
        <p className="mt-2 font-semibold text-white">
          You accepted — the new seller must confirm within 2 hours
        </p>
        <div className="mt-4 grid gap-2 text-sm text-slate-300">
          <p>
            Price: <strong className="text-white">{formatPrice(r.replacementPrice)}</strong>
          </p>
          <p className="flex items-center gap-2">
            <FaMapMarkerAlt className="text-amber-400" />
            Seller city: <strong>{r.replacementSellerCity || '—'}</strong>
          </p>
          <p className="flex items-center gap-2 text-amber-200">
            <FaSpinner className="animate-spin" />
            Seller accepts in <strong>{formatCountdown(remaining)}</strong>
          </p>
          {r.attemptNumber > 1 && (
            <p className="text-xs text-amber-300/80">
              Reassignment attempt {r.attemptNumber} of 5
            </p>
          )}
        </div>
        <p className="mt-3 text-xs text-slate-400">
          If the seller does not respond in time, we will find the next available seller automatically.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-6 rounded-2xl border border-blue-600/50 bg-blue-950/30 p-5">
      <p className="text-sm font-bold uppercase tracking-wide text-blue-300">
        Replacement seller found
      </p>
      <p className="mt-2 font-semibold text-white">
        We found another seller for the same part
      </p>

      <div className="mt-4 grid gap-2 text-sm text-slate-300">
        <p>
          Price: <strong className="text-white">{formatPrice(r.replacementPrice)}</strong>
          {priceDiff > 0 && (
            <span className="text-amber-300"> (+{formatPrice(priceDiff)} vs original)</span>
          )}
          {priceDiff < 0 && (
            <span className="text-emerald-300"> ({formatPrice(Math.abs(priceDiff))} cheaper)</span>
          )}
        </p>
        <p className="flex items-center gap-2">
          <FaMapMarkerAlt className="text-blue-400" />
          Seller city: <strong>{r.replacementSellerCity || '—'}</strong>
        </p>
        <p>
          Dispatch window: <strong>{r.dispatchWindowHours || 48}h</strong>
        </p>
        {r.attemptNumber > 1 && (
          <p className="text-xs text-blue-300/80">
            Reassignment attempt {r.attemptNumber} of 5
          </p>
        )}
        <p className="flex items-center gap-2 text-blue-200">
          <FaClock />
          Respond within <strong>{formatCountdown(remaining)}</strong> or escrow is refunded
        </p>
      </div>

      {needsApproval && (
        <p className="mt-3 text-xs text-amber-200">
          Higher price — accepting will charge the difference to your original payment method.
        </p>
      )}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          disabled={acting}
          onClick={handleAccept}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-50"
        >
          <FaCheck /> Accept replacement
        </button>
        <button
          type="button"
          disabled={acting}
          onClick={handleReject}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-500 py-3 text-sm font-semibold text-slate-300 hover:bg-slate-800 disabled:opacity-50"
        >
          <FaTimes /> Decline &amp; get refund
        </button>
      </div>
    </section>
  );
}
